import { useMemo } from "react";
import User from "lucide-react/dist/esm/icons/user";
import RotateCcw from "lucide-react/dist/esm/icons/rotate-ccw";
import Check from "lucide-react/dist/esm/icons/check";
import { cn } from "@/lib/utils";
import type { ApartmentWithTranslation } from "@/hooks/useApartments";

export function parseSize(size: string): number | null {
  const m = size?.match?.(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

interface Props {
  apartments: ApartmentWithTranslation[];
  selectedGuests: number | null;
  selectedSize: number | null;
  selectedFeatures: string[];
  onGuestsChange: (g: number | null) => void;
  onSizeChange: (s: number | null) => void;
  onFeaturesChange: (f: string[]) => void;
}

const ApartmentFilter = ({
  apartments,
  selectedGuests,
  selectedSize,
  selectedFeatures,
  onGuestsChange,
  onSizeChange,
  onFeaturesChange,
}: Props) => {
  const guestOptions = useMemo(() => {
    const set = new Set<number>();
    apartments.forEach((a) => a.maxGuests && set.add(a.maxGuests));
    return Array.from(set).sort((a, b) => a - b);
  }, [apartments]);

  const sizeOptions = useMemo(() => {
    const set = new Set<number>();
    apartments.forEach((a) => {
      const n = parseSize(a.size);
      if (n) set.add(n);
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [apartments]);

  const featureOptions = useMemo(() => {
    const set = new Set<string>();
    apartments.forEach((a) => a.features?.forEach((f) => f && set.add(f)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "de"));
  }, [apartments]);

  const matchesGuestsSize = (a: ApartmentWithTranslation) =>
    (selectedGuests === null || a.maxGuests === selectedGuests) &&
    (selectedSize === null || parseSize(a.size) === selectedSize);

  const matchesAllFeatures = (a: ApartmentWithTranslation, extra?: string) => {
    const required = extra ? [...selectedFeatures, extra] : selectedFeatures;
    return required.every((f) => a.features?.includes(f));
  };

  const sizeCountFor = (size: number) =>
    apartments.filter(
      (a) =>
        parseSize(a.size) === size &&
        (selectedGuests === null || a.maxGuests === selectedGuests) &&
        matchesAllFeatures(a)
    ).length;

  const guestCountFor = (g: number) =>
    apartments.filter(
      (a) =>
        a.maxGuests === g &&
        (selectedSize === null || parseSize(a.size) === selectedSize) &&
        matchesAllFeatures(a)
    ).length;

  const featureCountFor = (f: string) => {
    const active = selectedFeatures.includes(f);
    return apartments.filter(
      (a) => matchesGuestsSize(a) && (active ? matchesAllFeatures(a) : matchesAllFeatures(a, f))
    ).length;
  };

  const hasActive =
    selectedGuests !== null || selectedSize !== null || selectedFeatures.length > 0;

  const toggleFeature = (f: string) => {
    if (selectedFeatures.includes(f)) {
      onFeaturesChange(selectedFeatures.filter((x) => x !== f));
    } else {
      onFeaturesChange([...selectedFeatures, f]);
    }
  };

  const itemBase =
    "relative inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold font-body shrink-0 " +
    "bg-white/10 text-white transition-colors duration-200 hover:bg-white/20 min-h-[36px]";
  const itemActive = "bg-white/25 text-white ring-2 ring-white";
  const itemDisabled = "opacity-[0.15] cursor-not-allowed hover:bg-white/10";

  return (
    <nav
      aria-label="Ferienwohnungen filtern"
      className="mb-10 rounded-2xl overflow-hidden shadow-lg shadow-ocean-dark/20 bg-ocean-dark w-full max-w-full"
    >
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-center gap-y-3 sm:gap-x-6 sm:gap-y-2 px-3 py-4 sm:px-6 sm:py-5">
        {/* Personen group */}
        <div className="flex items-center gap-1.5 overflow-x-auto sm:overflow-visible -mx-1 px-1 sm:mx-0 sm:px-0 scrollbar-thin">
          <button
            type="button"
            onClick={() => {
              if (!hasActive) return;
              onGuestsChange(null);
              onSizeChange(null);
              onFeaturesChange([]);
            }}
            disabled={!hasActive}
            aria-disabled={!hasActive}
            aria-label="Filter zurücksetzen"
            className={cn(
              "inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors shrink-0",
              hasActive
                ? "bg-white/10 text-white hover:bg-white/20 cursor-pointer"
                : "bg-white/5 text-white/30 cursor-not-allowed"
            )}
          >
            <RotateCcw className="w-4 h-4" strokeWidth={2.5} />
          </button>
          <span className="hidden sm:inline-block text-[10px] sm:text-xs font-bold font-body uppercase tracking-[0.15em] sm:tracking-[0.18em] text-white/70 mr-1 sm:mr-2 shrink-0">
            Personen
          </span>
          {guestOptions.map((g) => {
            const active = selectedGuests === g;
            const count = guestCountFor(g);
            const disabled = count === 0 && !active;
            return (
              <button
                key={g}
                type="button"
                onClick={() => !disabled && onGuestsChange(active ? null : g)}
                disabled={disabled}
                aria-pressed={active}
                aria-label={`${g} Personen`}
                className={cn(itemBase, active && itemActive, disabled && itemDisabled)}
              >
                <span className="flex -space-x-1">
                  {Array.from({ length: g }).map((_, i) => (
                    <User key={i} className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={2} />
                  ))}
                </span>
                <span className="tabular-nums">{g}</span>
              </button>
            );
          })}
        </div>

        {/* Divider */}
        {guestOptions.length > 0 && sizeOptions.length > 0 && (
          <span className="hidden sm:inline-block h-5 w-px bg-white/20" aria-hidden />
        )}

        {/* Wohnfläche group — pills auf Tablet/Desktop */}
        <div className="hidden sm:flex items-center gap-1.5">
          <span className="text-xs font-bold font-body uppercase tracking-[0.18em] text-white/70 mr-2 shrink-0">
            Wohnfläche
          </span>
          {sizeOptions.map((s) => {
            const active = selectedSize === s;
            const count = sizeCountFor(s);
            const disabled = count === 0 && !active;
            return (
              <button
                key={s}
                type="button"
                onClick={() => !disabled && onSizeChange(active ? null : s)}
                disabled={disabled}
                aria-pressed={active}
                className={cn(itemBase, active && itemActive, disabled && itemDisabled)}
              >
                <span className="tabular-nums">{s} m²</span>
                <span className="text-xs tabular-nums text-white/60">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Wohnfläche — Dropdown nur auf Smartphones */}
        <div className="flex sm:hidden items-center gap-2 w-full">
          <span className="text-[10px] font-bold font-body uppercase tracking-[0.15em] text-white/70 shrink-0">
            Wohnfläche
          </span>
          <div className="relative flex-1 min-w-0">
            <select
              value={selectedSize ?? ""}
              onChange={(e) => onSizeChange(e.target.value === "" ? null : Number(e.target.value))}
              aria-label="Wohnfläche auswählen"
              className={cn(
                "w-full appearance-none rounded-full text-white text-xs font-semibold font-body",
                "pl-4 pr-10 py-2.5 min-h-[40px] border border-white/15",
                "bg-white/10 backdrop-blur-sm",
                "transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/15 focus:border-white/25",
                "active:scale-[0.98]",
                selectedSize !== null
                  ? "bg-white/25 ring-2 ring-white border-transparent shadow-[0_0_12px_rgba(255,255,255,0.15)]"
                  : "hover:bg-white/[0.14]"
              )}
            >
              <option value="" className="bg-ocean-dark text-white">Alle Größen</option>
              {sizeOptions.map((s) => {
                const count = sizeCountFor(s);
                const disabled = count === 0 && selectedSize !== s;
                return (
                  <option
                    key={s}
                    value={s}
                    disabled={disabled}
                    className="bg-ocean-dark text-white"
                  >
                    {s} m² ({count})
                  </option>
                );
              })}
            </select>
            <svg
              aria-hidden
              viewBox="0 0 24 24"
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>

        {/* Features — Multiauswahl */}
        {featureOptions.length > 0 && (
          <>
            <span className="hidden sm:inline-block h-5 w-px bg-white/20" aria-hidden />
            <div className="flex items-center gap-1.5 flex-wrap w-full sm:w-auto">
              <span className="text-[10px] sm:text-xs font-bold font-body uppercase tracking-[0.15em] sm:tracking-[0.18em] text-white/70 mr-1 sm:mr-2 shrink-0">
                Ausstattung
              </span>
              {featureOptions.map((f) => {
                const active = selectedFeatures.includes(f);
                const count = featureCountFor(f);
                const disabled = count === 0 && !active;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => !disabled && toggleFeature(f)}
                    disabled={disabled}
                    aria-pressed={active}
                    className={cn(itemBase, active && itemActive, disabled && itemDisabled)}
                  >
                    {active && <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={3} />}
                    <span>{f}</span>
                    <span className="text-xs tabular-nums text-white/60">({count})</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </nav>
  );
};

export default ApartmentFilter;
